<template>
  <div class="field-group">
    <template v-if="isArray">
      <div class="group-fields">
        <div
          v-for="(groupItem, groupIndex) in value"
          :key="`group-${groupIndex}`"
          class=""
        >
          <div
            v-for="subField in getFilteredGroupFields(groupItem)"
            :key="`${groupIndex}-${subField.key}`"
            class="group-field"
          >
            <small class="text-muted">{{ subField.label }}:</small>
            <span>
              <!-- Grupo anidado -->
              <template v-if="subField.type === 'group' && subField.fields">
                <FieldGroup
                  :value="groupItem[subField.key]"
                  :fields="subField.fields"
                  :isArray="subField.isArray"
                  :referenceItems="referenceItems"
                  :modelName="modelName"
                  :item="item"
                  class="nested-group"
                />
              </template>
              <!-- Array de valores: cada item se renderiza con el componente (sin unir por coma). Si llega string "a, b" se normaliza a array. -->
              <template v-else-if="subField.isArray && getFieldComponent(subField) && getListArrayValuesForGroup(groupItem[subField.key]).length > 0">
                <span
                  v-for="(arrItem, arrIdx) in getListArrayValuesForGroup(groupItem[subField.key])"
                  :key="arrIdx"
                  class="group-array-item d-inline-block mr-1"
                >
                  <component
                    :is="getFieldComponent(subField)"
                    :value="arrItem"
                    :field="subField"
                    :referenceItems="referenceItems"
                    :refModel="getRefModel(subField)"
                    :isGroupModel="false"
                    :modelName="modelName"
                    :item="item"
                    mode="view"
                    @open-reference-modal="$emit('go-to-reference', $event)"
                    @go-to-blnumber="$emit('go-to-blnumber', $event)"
                  />
                </span>
              </template>
              <!-- Componente normal (no array) -->
              <component
                v-else-if="groupItem[subField.key] && !subField.isArray && getFieldComponent(subField)"
                :is="getFieldComponent(subField)"
                :value="groupItem[subField.key]"
                :field="subField"
                :referenceItems="referenceItems"
                :refModel="getRefModel(subField)"
                :isArray="subField.isArray"
                :isGroupModel="false"
                :modelName="modelName"
                :item="item"
                @open-reference-modal="$emit('go-to-reference', $event)"
                @go-to-blnumber="$emit('go-to-blnumber', $event)"
              />
              <template v-else-if="subField.isArray && Array.isArray(groupItem[subField.key])">
                {{ groupItem[subField.key].join(', ') }}
              </template>
              <template v-else>
                {{ groupItem[subField.key] }}
              </template>
            </span>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="group-fields">
        <div
          v-for="subField in getFilteredItemGroupFields()"
          :key="subField.key"
          class="group-field"
        >
          <small class="text-muted">{{ subField.label }}:</small>
          <span>
            <!-- Grupo anidado -->
            <template v-if="subField.type === 'group' && subField.fields">
              <FieldGroup
                :value="value[subField.key]"
                :fields="subField.fields"
                :isArray="subField.isArray"
                :referenceItems="referenceItems"
                :modelName="modelName"
                :item="item"
                class="nested-group"
              />
            </template>
            <!-- Array de valores: cada item se renderiza con el componente (sin unir por coma). Si llega string "a, b" se normaliza a array. -->
            <template v-else-if="subField.isArray && getFieldComponent(subField) && getListArrayValuesForGroup(value?.[subField.key]).length > 0">
              <span
                v-for="(arrItem, arrIdx) in getListArrayValuesForGroup(value[subField.key])"
                :key="arrIdx"
                class="group-array-item d-inline-block mr-1"
              >
                <component
                  :is="getFieldComponent(subField)"
                  :value="arrItem"
                  :field="subField"
                  :referenceItems="referenceItems"
                  :refModel="getRefModel(subField)"
                  :isGroupModel="false"
                  :modelName="modelName"
                  :item="item"
                  mode="view"
                  @open-reference-modal="$emit('go-to-reference', $event)"
                  @go-to-blnumber="$emit('go-to-blnumber', $event)"
                />
              </span>
            </template>
            <!-- Componente normal (no array) -->
            <component
              v-else-if="value?.[subField.key] && !subField.isArray && getFieldComponent(subField)"
              :is="getFieldComponent(subField)"
              :value="value[subField.key]"
              :field="subField"
              :referenceItems="referenceItems"
              :refModel="getRefModel(subField)"
              :isArray="subField.isArray"
              :isGroupModel="false"
              :modelName="modelName"
              :item="item"
              @open-reference-modal="$emit('go-to-reference', $event)"
              @go-to-blnumber="$emit('go-to-blnumber', $event)"
            />
            <template v-else>
              {{ value?.[subField.key] }}
            </template>
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import FieldReference from "./FieldReference.vue";
import FieldList from "./FieldList.vue";
import FieldText from "./FieldText.vue";
import { FieldText as FieldTextCore } from "../model-definition-core";

export default {
  name: "FieldGroup",
  components: {
    FieldReference,
    FieldList,
    FieldText,
    FieldTextCore,
    // Self-reference para grupos anidados
    FieldGroup: () => import('./FieldGroup.vue'),
  },
  props: {
    value: {
      type: [Object, Array],
    },
    fields: {
      type: Array,
      required: true,
    },
    isArray: {
      type: Boolean,
      default: false,
    },
    referenceItems: {
      type: [Object, Array],
      default: () => ({}),
    },
    modelName: {
      type: String,
      default: "",
    },
    item: {
      type: Object,
      default: null,
    },
  },
  methods: {
    getFilteredGroupFields(groupItem) {
      return this.fields.filter(
        (subField) =>
          groupItem &&
          groupItem[subField.key] !== null &&
          groupItem[subField.key] !== undefined &&
          groupItem[subField.key] !== ""
      );
    },
    getFilteredItemGroupFields() {
      return this.fields.filter(
        (subField) =>
          this.value &&
          this.value[subField.key] !== null &&
          this.value[subField.key] !== undefined &&
          this.value[subField.key] !== ""
      );
    },
    getFieldComponent(field) {
      switch (field.type) {
        case "reference":
          return "FieldReference";
        case "string":
        case "text":
        case "email":
          return "FieldTextCore";
        case "list":
          return "FieldList";
        case "group":
          return "FieldGroup";
        case "boolean":
        case "text":
        case "number":
        case "date":
        case "image":
        case "file":
          return "FieldText";
        default:
          return null;
      }
    },
    /** Si el valor de list isArray llega como string "a, b", normalizar a [a, b] para pasar cada ítem al componente. */
    getListArrayValuesForGroup(raw) {
      if (raw == null || raw === "") return [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        return raw.split(/\s*,\s*/).map((s) => s.trim()).filter(Boolean);
      }
      return [raw];
    },
    getRefModel(field) {
      // Si el campo tiene ref definido, usarlo
      if (field.ref) {
        return field.ref;
      }

      // Si no, inferir del nombre del campo
      if (field.key === "shippings") {
        return "shipping";
      }
      if (field.key === "proforms") {
        return "proform";
      }
      if (field.key === "clients") {
        return "client";
      }

      return field.key;
    },
  },
};
</script>

<style scoped>
.field-group .group-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-group .group-field {
  display: flex;
  flex-direction: column;
}

.field-group .group-field small {
  font-size: 0.8em;
  color: #6c757d;
}

.field-group .group-field span {
  font-size: 0.9em;
}

.field-group .group-item {
  background-color: rgba(0, 0, 0, 0.02);
  padding: 0.5rem;
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
}

/* Estilos para grupos anidados */
.nested-group {
  margin-left: 8px;
  padding-left: 8px;
  border-left: 2px solid #dee2e6;
  margin-top: 4px;
}

.nested-group .group-field small {
  font-size: 0.75em;
}

.nested-group .group-field span {
  font-size: 0.85em;
}
</style>
